import { Input } from '../Input';
import { Label } from '../Label';

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Button } from '../Button';
import { cn } from '@ais-ui/utils';
import { DurationField, DurationPickerProps } from './types';

interface DayTimeDurationPickerProps {
  value: string;
  onChange: (value: string) => void;
}

export const DayTimeDurationPicker = ({
  value,
  onChange,
}: DayTimeDurationPickerProps) => {
  const [days, setDays] = useState<string>('');
  const [time, setTime] = useState<string>('');

  // Parse the incoming value
  useEffect(() => {
    if (!value) {
      return;
    }

    const parts = value.split(' ');
    if (parts.length === 2) {
      setDays(parts[0]);
      setTime(parts[1]);
    }
  }, [value]);

  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDays = e.target.value;
    setDays(newDays);
    onChange(`${newDays} ${time}`);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTime(newTime);
    onChange(`${days} ${newTime}`);
  };

  return (
    <div className="flex space-x-4">
      <div className="flex flex-col space-y-2">
        <Label className="text-muted-foreground" htmlFor="days">
          Days
        </Label>
        <Input
          className="w-44"
          id="days"
          type="number"
          value={days}
          onChange={handleDaysChange}
          placeholder="Enter number of days"
        />
      </div>
      <div className="flex flex-col space-y-2">
        <Label className="text-muted-foreground" htmlFor="time">
          Time (HH:MM)
        </Label>
        <Input
          id="time"
          value={time}
          onChange={handleTimeChange}
          placeholder="Enter time in HH:MM"
        />
      </div>
    </div>
  );
};

interface TimeDurationPickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  format: 'HH:MM' | 'HH:MM:SS';
  className?: string;
}

export const TimeDurationPicker = ({
  value,
  onChange,
  format,
  className,
}: TimeDurationPickerProps) => {
  const [time, setTime] = useState<string | undefined>('');

  // Parse the incoming value
  useEffect(() => {
    setTime(value);
  }, [value]);

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value === '' ? undefined : e.target.value;

    setTime(newTime);
    onChange(newTime);
  };

  const placeholderText =
    format === 'HH:MM' ? 'Enter time in HH:MM' : 'Enter time in HH:MM:SS';

  return (
    <div className={cn('flex space-x-4', className)}>
      <div className="flex flex-col space-y-2 w-full">
        <Input
          id="time"
          step={format === 'HH:MM' ? '60' : '1'} // Adjust step for seconds input
          value={time || ''}
          onChange={handleTimeChange}
          placeholder={placeholderText}
        />
      </div>
    </div>
  );
};

export function DurationPicker({
  value,
  onChange,
  maxHours = 24,
  minHours = 0,
  minuteStep = 5,
}: DurationPickerProps) {
  // Use local state that's initialized from props
  const [duration, setDuration] = useState(value);

  // Sync with external value when it changes
  useEffect(() => {
    setDuration(value);
  }, [value]);

  // Helper to call onChange with new values
  const handleChange = (newDuration: { hours: number; minutes: number }) => {
    setDuration(newDuration);
    if (onChange) {
      onChange(newDuration);
    }
  };

  const incrementHours = () => {
    const newDuration = {
      ...duration,
      hours: Math.min(duration.hours + 1, maxHours),
    };
    handleChange(newDuration);
  };

  const decrementHours = () => {
    const newDuration = {
      ...duration,
      hours: Math.max(duration.hours - 1, minHours),
    };
    handleChange(newDuration);
  };

  const incrementMinutes = () => {
    if (duration.minutes === 60 - minuteStep) {
      const newDuration = {
        hours: Math.min(duration.hours + 1, maxHours),
        minutes: 0,
      };
      handleChange(newDuration);
    } else {
      const newDuration = {
        ...duration,
        minutes: duration.minutes + minuteStep,
      };
      handleChange(newDuration);
    }
  };

  const decrementMinutes = () => {
    if (duration.minutes === 0) {
      if (duration.hours > minHours) {
        const newDuration = {
          hours: duration.hours - 1,
          minutes: 60 - minuteStep,
        };
        handleChange(newDuration);
      }
    } else {
      const newDuration = {
        ...duration,
        minutes: duration.minutes - minuteStep,
      };
      handleChange(newDuration);
    }
  };

  const handleInputChange = (field: DurationField, value: string) => {
    const numValue = parseInt(value, 10);

    if (isNaN(numValue)) return;

    if (field === 'hours') {
      const newDuration = {
        ...duration,
        hours: Math.min(Math.max(numValue, minHours), maxHours),
      };
      handleChange(newDuration);
    } else if (field === 'minutes') {
      // Round to nearest step
      const roundedMinutes = Math.round(numValue / minuteStep) * minuteStep;
      const validMinutes = Math.min(Math.max(roundedMinutes, 0), 59);

      const newDuration = {
        ...duration,
        minutes: validMinutes,
      };
      handleChange(newDuration);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-36 justify-between text-left font-normal"
        >
          <span>{`${duration.hours}h ${duration.minutes}m`}</span>
          <div className="flex flex-col">
            <ChevronUp
              className="h-3 w-3 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                incrementMinutes();
              }}
            />
            <ChevronDown
              className="h-3 w-3 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (duration.hours > 0 || duration.minutes > 0) {
                  decrementMinutes();
                }
              }}
            />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-52 p-3" align="end">
        <div className="flex gap-4">
          <div className="flex-1">
            <h4 className="text-sm text-muted-foreground mb-1">Hours</h4>
            <div className="flex space-x-2">
              <Input
                className="w-12 h-8"
                value={duration.hours}
                onChange={(e) => handleInputChange('hours', e.target.value)}
              />
              <div className="flex flex-col space-y-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={incrementHours}
                  className="h-3 w-3 p-0"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={decrementHours}
                  disabled={duration.hours === 0 && duration.minutes === 0}
                  className="h-3 w-3 p-0"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-sm text-muted-foreground mb-1">Minutes</h4>
            <div className="flex space-x-2">
              <Input
                className="w-12 h-8"
                value={duration.minutes}
                onChange={(e) => handleInputChange('minutes', e.target.value)}
              />
              <div className="flex flex-col space-y-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={incrementMinutes}
                  className="h-3 w-3 p-0"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={decrementMinutes}
                  disabled={duration.hours === 0 && duration.minutes === 0}
                  className="h-3 w-3 p-0"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
